import {
  RID,
  ACCESS_TOKEN,
  getNewTokenRequestBody,
} from '~/models/AccessCookies.model';

type JwtPayload = {
  nbf?: number;
  exp?: number;
};

export const useInitialCookies = () => {
  const { functionalConsented, hasInteractedWithCookieConsentBox } =
    useCookieConsent();

  const config = useRuntimeConfig();
  const apiBaseUrl = config.public.apiBaseUrl;
  const ridEnvironmentVarValue = config.public.RID;

  const decodeJwt = (token: string): JwtPayload => {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = atob(payload);
      return JSON.parse(decodedPayload);
    } catch (e) {
      return {};
    }
  };

  const fetchToken = async (bearerToken?: string) => {
    const headers = bearerToken
      ? { Authorization: `Bearer ${bearerToken}` }
      : {};

    try {
      const response = await $fetch<{ token: string }>(
        `${apiBaseUrl}/api-frontend/Authenticate/GetToken`,
        {
          method: 'POST',
          headers: headers as Headers,
          body: getNewTokenRequestBody,
        },
      );

      const encodedTokenValue = response.token;
      const decodedTokenValue = decodeJwt(encodedTokenValue);
      const expirationDate = decodedTokenValue.exp
        ? new Date(+(decodedTokenValue.exp || 0) * 1000)
        : undefined;

      useCookie(ACCESS_TOKEN, {
        expires: functionalConsented.value ? expirationDate : undefined,
        secure: true,
      }).value = response.token;
    } catch (error) {
      // Do nothing, let factory.ts try to set the token
    }
  };

  const setAccessTokenCookie = async () => {
    const tokenCookie = useCookie(ACCESS_TOKEN);
    if (tokenCookie.value) {
      const decodedToken = decodeJwt(tokenCookie.value);
      const expirationDateSeconds = +(decodedToken.exp || 0);
      const currentTimeSeconds = Math.floor(Date.now() / 1000);

      if (
        expirationDateSeconds &&
        currentTimeSeconds >= expirationDateSeconds
      ) {
        await fetchToken();
      }
    } else {
      await fetchToken();
    }
  };

  const setRoutedServerForSessionCookie = () => {
    const routedServerForSessionCookie = useCookie(RID, { secure: true });

    if (!routedServerForSessionCookie.value) {
      if (ridEnvironmentVarValue) {
        routedServerForSessionCookie.value = `${ridEnvironmentVarValue}`;
      }
    }
  };

  onMounted(() => {
    if (import.meta.client) {
      setTimeout(() => {
        setRoutedServerForSessionCookie();
      }, 0);

      watch(
        hasInteractedWithCookieConsentBox,
        (hasMadeAConsentChoiceValue) => {
          if (hasMadeAConsentChoiceValue) {
            setAccessTokenCookie();
          }
        },
        { immediate: true },
      );
    }
  });
};
